import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ROUTES } from '@/constants';
import { authService } from '@/services/api/auth.service';
import { getAccessToken } from '@/services/api/tokens';
import { useAppDispatch } from '@/store/hooks';
import { sessionStarted } from '@/store/slices';
import type { MfaVerifyRequest } from '@/types';
import { queryKeys } from './keys';

/**
 * Server state for the second factor.
 *
 * Split from useAuthQueries because these are two different jobs: one completes
 * a login for somebody who has no session yet, the rest manage a factor for
 * somebody who does.
 */

export function useVerifyMfa() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: MfaVerifyRequest) => authService.verifyMfa(body),
    onSuccess: async (result) => {
      // Identical to the tail of useLogin, because this IS the rest of that
      // login - the same session, arriving one request later.
      // The whole cache, as in useLogin: no key is scoped to a user.
      queryClient.clear();

      const token = getAccessToken();
      if (token) dispatch(sessionStarted({ user: result.user, accessToken: token }));

      await navigate(ROUTES.dashboard, { replace: true });
    },
  });
}

export function useMfaStatus() {
  return useQuery({
    queryKey: queryKeys.auth.mfa(),
    queryFn: ({ signal }) => authService.mfaStatus(signal),
    staleTime: 60_000,
  });
}

export function useBeginMfaEnrollment() {
  return useMutation({ mutationFn: () => authService.beginMfaEnrollment() });
}

export function useConfirmMfaEnrollment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) => authService.confirmMfaEnrollment(code),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.mfa() });
      toast.success('Two-factor authentication is on', {
        description: 'You will be asked for a code the next time you sign in.',
      });
    },
  });
}

export function useDisableMfa() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) => authService.disableMfa(code),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.mfa() });
      toast.success('Two-factor authentication is off');
    },
  });
}
